/* ============================================================================
 * La Botte Gardiane — custom storefront behaviours
 * ==========================================================================*/

/**
 * <lbg-product-sizes> — product card size list.
 *
 * Shows a limited number of sizes; the remaining ones (marked with
 * `.lbg-pc__size--extra` and the `hidden` attribute) sit behind a "+N tailles"
 * button. Clicking the button reveals every size.
 *
 * (The colours row uses a plain non-interactive "+N couleurs" text indicator,
 * so it has no matching custom element.)
 */
class LbgProductSizes extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-lbg-pc-more-sizes]');
    if (!this.button) return;

    this.onClick = () => this.expand();
    this.button.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    if (this.button && this.onClick) {
      this.button.removeEventListener('click', this.onClick);
    }
  }

  expand() {
    this.querySelectorAll('.lbg-pc__size--extra').forEach((size) => {
      size.hidden = false;
    });

    this.button.setAttribute('aria-expanded', 'true');
    this.button.hidden = true;
  }
}

if (!customElements.get('lbg-product-sizes')) {
  customElements.define('lbg-product-sizes', LbgProductSizes);
}

/**
 * <lbg-customize-drawer> — « Personnaliser mon modèle » multi-step drawer.
 *
 * Extends the theme's `Drawer` element (registered as `x-drawer`) so it inherits
 * focus-trap, scroll-lock, ESC / overlay-click handling and the slide / bottom-sheet
 * animations. Each step is one product from `custom.customization_options`; the
 * customer picks one variant per step. When every step is filled, it adds the base
 * product plus one nested cart line per selected variant (native Shopify nested cart
 * lines via the AJAX Cart API `parent_id` field).
 *
 * Opens on the `lbg:customize:open` event dispatched on `document` by the
 * `customize_button` block (also covers the `?customize=1` auto-open).
 */
customElements.whenDefined('x-drawer').then(() => {
  const Drawer = customElements.get('x-drawer');

  class LbgCustomizeDrawer extends Drawer {
    // NOTE: the base Drawer moves this element to <body> on open, which re-runs
    // disconnected/connectedCallback and aborts the previous listeners. So all
    // interaction state is derived from the DOM (see the `selections` and
    // `currentStep` getters) rather than kept in instance fields — this way a
    // re-append never wipes the customer's progress. connectedCallback only
    // caches element refs and (re)binds listeners with the fresh abort signal.
    connectedCallback() {
      super.connectedCallback();

      this.tabs = Array.from(this.querySelectorAll('[data-lbg-customize-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-lbg-customize-panel]'));
      this.backButton = this.querySelector('[data-lbg-customize-back]');
      this.nextButton = this.querySelector('[data-lbg-customize-next]');
      this.submitButton = this.querySelector('[data-lbg-customize-submit]');
      this.totalEl = this.querySelector('[data-lbg-customize-total]');

      this.basePrice = parseInt(this.submitButton?.dataset.basePrice || '0', 10);

      // Fallback base variant; kept in sync with the product page's variant picker.
      this.baseVariantId = this.dataset.baseVariantId || null;
      document.addEventListener(
        'variant:change',
        (event) => {
          const variant = event.detail && event.detail.variant;
          if (variant && variant.id) this.baseVariantId = String(variant.id);
        },
        { signal: this.abortController.signal },
      );

      // Open when the trigger (or ?customize=1 auto-open) asks for it.
      document.addEventListener('lbg:customize:open', () => this.show(), {
        signal: this.abortController.signal,
      });

      // Delegate every interaction inside the drawer.
      this.addEventListener('click', this.onClick.bind(this), {
        signal: this.abortController.signal,
      });

      // Keyboard activation of the tooltip trigger (role="button").
      this.addEventListener(
        'keydown',
        (event) => {
          const trigger = event.target.closest('[data-lbg-customize-tooltip-trigger]');
          if (!trigger) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.toggleTooltip(trigger);
          }
        },
        { signal: this.abortController.signal },
      );

      // Close any open tooltip when clicking outside of a tooltip trigger.
      document.addEventListener(
        'click',
        (event) => {
          if (!event.target.closest('[data-lbg-customize-tooltip-trigger]')) this.closeTooltips();
        },
        { signal: this.abortController.signal },
      );

      this.refreshBaseAvailability();
      this.refreshControls();
    }

    get stepCount() {
      return this.panels.length;
    }

    // Index of the currently visible panel.
    get currentStep() {
      const index = this.panels.findIndex((panel) => !panel.hidden);
      return index === -1 ? 0 : index;
    }

    // One entry per step: the `.is-selected` option in that panel, or null.
    // Used for step gating / navigation, so it covers every panel including the
    // base option ones (whose chips carry an option value, not a variant id).
    // Cart lines and pricing use childSelections / baseVariant below.
    get selections() {
      return this.panels.map((panel) => {
        const option = panel.querySelector('[data-lbg-customize-option].is-selected');
        if (!option) return null;
        return {
          variantId: option.dataset.variantId || null,
          price: parseInt(option.dataset.variantPrice || '0', 10),
        };
      });
    }

    // Base option panels (Pointure, Couleur boucle…) select a variant of the *base*
    // product rather than a nested child line: together they resolve to one variant,
    // which drives baseVariantId and the base price of the total.
    get basePanels() {
      return this.panels.filter((panel) => panel.hasAttribute('data-lbg-customize-base-panel'));
    }

    // { [option position]: selected value } for the base options picked so far.
    get baseOptionValues() {
      const values = {};
      this.basePanels.forEach((panel) => {
        const option = panel.querySelector('[data-lbg-customize-option].is-selected');
        if (option) values[panel.dataset.optionPosition] = option.dataset.optionValue;
      });
      return values;
    }

    // The base product's variants with their option values, from the Liquid matrix.
    get variantMatrix() {
      if (!this._variantMatrix) {
        const script = this.querySelector('[data-lbg-customize-variants]');
        try {
          this._variantMatrix = script ? JSON.parse(script.textContent) : [];
        } catch (error) {
          this._variantMatrix = [];
        }
      }
      return this._variantMatrix;
    }

    // Variants matching every option value in `values` (option position -> value).
    variantsMatching(values) {
      const positions = Object.keys(values);
      return this.variantMatrix.filter((variant) =>
        positions.every((position) => variant.options[position - 1] === values[position]),
      );
    }

    // The base variant, once every base option has been picked. Normally exactly one
    // variant matches; should the product ever carry an option that isn't a base step,
    // several do — prefer an available one so we never add a sold-out variant.
    get baseVariant() {
      const values = this.baseOptionValues;
      if (Object.keys(values).length !== this.basePanels.length) return null;
      const matches = this.variantsMatching(values);
      return matches.find((variant) => variant.available) || matches[0] || null;
    }

    // Selections that become nested child cart lines: every step except the base ones.
    get childSelections() {
      return this.panels
        .filter((panel) => !panel.hasAttribute('data-lbg-customize-base-panel'))
        .map((panel) => {
          const option = panel.querySelector('[data-lbg-customize-option].is-selected');
          if (!option) return null;
          return {
            variantId: option.dataset.variantId,
            price: parseInt(option.dataset.variantPrice || '0', 10),
          };
        });
    }

    onClick(event) {
      // Tooltip trigger sits inside an option button; toggle it (for touch) without
      // letting the click bubble up and select the variant.
      const tooltipTrigger = event.target.closest('[data-lbg-customize-tooltip-trigger]');
      if (tooltipTrigger) {
        event.preventDefault();
        this.toggleTooltip(tooltipTrigger);
        return;
      }

      const option = event.target.closest('[data-lbg-customize-option]');
      if (option) {
        this.selectOption(option);
        return;
      }

      const tab = event.target.closest('[data-lbg-customize-tab]');
      if (tab && !tab.disabled) {
        this.goToStep(parseInt(tab.dataset.lbgCustomizeTab, 10));
        return;
      }

      if (event.target.closest('[data-lbg-customize-next]')) {
        if (this.currentStep < this.stepCount - 1) this.goToStep(this.currentStep + 1);
        return;
      }

      if (event.target.closest('[data-lbg-customize-back]')) {
        if (this.currentStep > 0) this.goToStep(this.currentStep - 1);
        return;
      }

      if (event.target.closest('[data-lbg-customize-submit]')) {
        this.addToCart();
      }
    }

    selectOption(option) {
      if (option.dataset.unavailable === 'true') return;

      const step = parseInt(option.dataset.step, 10);

      // Radio behaviour within the step (the DOM is the source of truth).
      this.panels[step]
        .querySelectorAll('[data-lbg-customize-option]')
        .forEach((el) => {
          const isSelected = el === option;
          el.classList.toggle('is-selected', isSelected);
          el.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });

      // Unlock the next step's tab now that this one is valid.
      const nextTab = this.tabs[step + 1];
      if (nextTab) nextTab.disabled = false;

      this.refreshBaseAvailability();
      this.refreshControls();
    }

    // Grey out base option values that no available variant offers alongside the
    // *other* base options already picked (e.g. a buckle color not made in 85cm).
    // Each panel is evaluated against the selections of the other panels only, so
    // the customer's own choice in that panel never disables its siblings.
    refreshBaseAvailability() {
      const selected = this.baseOptionValues;

      this.basePanels.forEach((panel) => {
        const position = panel.dataset.optionPosition;
        const others = { ...selected };
        delete others[position];

        panel.querySelectorAll('[data-lbg-customize-option]').forEach((chip) => {
          const candidate = { ...others, [position]: chip.dataset.optionValue };
          const unavailable = !this.variantsMatching(candidate).some((variant) => variant.available);

          // Never disable the current selection: that would strand the customer on a
          // step with no valid choice. Changing another step clears it instead.
          if (unavailable && chip.classList.contains('is-selected')) {
            chip.classList.remove('is-selected');
            chip.setAttribute('aria-checked', 'false');
          }

          // The CSS keys off the "true" value, so set/remove rather than toggle.
          if (unavailable) {
            chip.setAttribute('data-unavailable', 'true');
            chip.setAttribute('aria-disabled', 'true');
          } else {
            chip.removeAttribute('data-unavailable');
            chip.removeAttribute('aria-disabled');
          }
        });
      });
    }

    // Toggle one option's tooltip overlay open (for touch); close the others.
    // Hover / focus still work on their own through CSS — this only drives the
    // tap-to-open state on the option card.
    toggleTooltip(trigger) {
      const option = trigger.closest('[data-lbg-customize-option]');
      if (!option) return;
      const willOpen = !option.classList.contains('is-tooltip-open');
      this.closeTooltips();
      option.classList.toggle('is-tooltip-open', willOpen);
      trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    }

    closeTooltips() {
      this.querySelectorAll('[data-lbg-customize-option].is-tooltip-open').forEach((option) => {
        option.classList.remove('is-tooltip-open');
        const trigger = option.querySelector('[data-lbg-customize-tooltip-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
    }

    goToStep(index) {
      if (index < 0 || index >= this.stepCount) return;
      // Cannot jump to a step that is still locked (previous step not filled).
      if (this.tabs[index] && this.tabs[index].disabled) return;

      // Update the DOM; the `currentStep` getter reads back from it.
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });

      this.refreshControls();
    }

    refreshControls() {
      const isLastStep = this.currentStep === this.stepCount - 1;
      const currentFilled = this.selections[this.currentStep] !== null;
      const allFilled = this.selections.every((selection) => selection !== null);

      // Back button: visible from the second step onward.
      if (this.backButton) this.backButton.hidden = this.currentStep === 0;

      // Next vs. Add-to-cart depending on whether we're on the last step.
      if (this.nextButton) {
        this.nextButton.hidden = isLastStep;
        this.nextButton.disabled = !currentFilled;
      }
      if (this.submitButton) {
        this.submitButton.hidden = !isLastStep;
        this.submitButton.disabled = !allFilled;
      }

      this.updateTotal();
    }

    updateTotal() {
      if (!this.totalEl) return;
      // Base price: with base option steps, it's the price of the resolved variant.
      // Until every base option is picked, preview the first-available variant price
      // (from data-base-preview-price) so the button already shows a price. Without
      // base option steps, use the fixed base variant price.
      let base = this.basePrice;
      const basePanels = this.basePanels;
      if (basePanels.length) {
        const variant = this.baseVariant;
        base = variant
          ? variant.price
          : parseInt(basePanels[0].dataset.basePreviewPrice || '0', 10);
      }
      const extras = this.childSelections.reduce(
        (sum, selection) => sum + (selection ? selection.price : 0),
        0,
      );
      this.totalEl.textContent = this.formatMoney(base + extras);
    }

    // Minimal implementation of Shopify's money format tokens, using the shop's
    // `money_format` string exposed by the theme (js-variables.liquid).
    formatMoney(cents) {
      const format =
        (window.themeVariables &&
          window.themeVariables.settings &&
          window.themeVariables.settings.moneyFormat) ||
        '${{amount}}';

      const withCommas = (number) =>
        number.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');

      return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, token) => {
        const value = cents / 100;
        switch (token) {
          case 'amount':
            return withCommas(value.toFixed(2));
          case 'amount_no_decimals':
            return withCommas(Math.round(value).toString());
          case 'amount_with_comma_separator':
            return value.toFixed(2).replace('.', ',').replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1.');
          case 'amount_no_decimals_with_comma_separator':
            return Math.round(value).toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1.');
          case 'amount_with_apostrophe_separator':
            return value.toFixed(2).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1'");
          default:
            return match;
        }
      });
    }

    get baseVariantIdLive() {
      // With base option steps, the base variant is the one matching every selected
      // option value. Otherwise fall back to the product page's variant picker.
      if (this.basePanels.length) {
        const variant = this.baseVariant;
        return variant ? String(variant.id) : null;
      }
      const form = this.dataset.productForm && document.getElementById(this.dataset.productForm);
      const field = form && form.querySelector('[name="id"]:not([disabled])');
      return (field && field.value) || this.baseVariantId;
    }

    async addToCart() {
      const baseId = this.baseVariantIdLive;
      // Every step must be filled (selections covers all panels, size included).
      if (!baseId || this.selections.some((selection) => selection === null)) return;

      this.submitButton.setAttribute('aria-busy', 'true');
      this.submitButton.disabled = true;

      // Parent line = base product (the chosen size variant); children = the
      // customization_options variants. The size selection is not a child line.
      const items = [{ id: Number(baseId), quantity: 1 }];
      this.childSelections.forEach((selection) => {
        items.push({
          id: Number(selection.variantId),
          quantity: 1,
          parent_id: Number(baseId),
        });
      });

      try {
        // Ask the theme's sections (cart drawer) to be re-rendered in the add response.
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          }),
        );

        const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items,
            sections: sectionsToBundle.join(','),
          }),
        });

        const addJson = await addResponse.json();

        if (!addResponse.ok) {
          this.dispatchEvent(
            new CustomEvent('cart:error', {
              bubbles: true,
              detail: { error: addJson.description || addJson.message },
            }),
          );
          return;
        }

        // Mirror the theme's ProductForm flow so the cart drawer refreshes and opens.
        const cart = await (
          await fetch(`${window.Shopify.routes.root}cart.js`)
        ).json();
        cart.sections = addJson.sections;

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              onSuccessDo: 'force_open_drawer',
              cart,
            },
          }),
        );

        this.reset();
        this.hide();
      } catch (error) {
        this.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: { error: String(error) },
          }),
        );
      } finally {
        this.submitButton.removeAttribute('aria-busy');
        this.submitButton.disabled = false;
      }
    }

    reset() {
      this.querySelectorAll('[data-lbg-customize-option].is-selected').forEach((el) => {
        el.classList.remove('is-selected');
        el.setAttribute('aria-checked', 'false');
      });
      this.tabs.forEach((tab, i) => {
        if (i > 0) tab.disabled = true;
      });
      this.goToStep(0);
    }
  }

  if (!customElements.get('lbg-customize-drawer')) {
    customElements.define('lbg-customize-drawer', LbgCustomizeDrawer);
  }
});
